// Smart category detector - IMPROVED with better detection
export const detectPlaceCategory = (tags, displayName) => {
  if (!tags) return 'place';

    // Handle both formats: direct tags and osm_key/osm_value
  if (tags.osm_key && tags.osm_value) {
    // Photon format: convert to standard format
    tags = {
      ...tags,
      [tags.osm_key]: tags.osm_value
    };
  }
  
  const name = (displayName || tags.name || '').toLowerCase();
  
  // Priority 0: Check for well-known fast food chains FIRST
  const fastFoodChains = [
    'mcdonalds', "mcdonald's", 'kfc', 'burger king', 'subway', 'pizza hut',
    'dominos', "domino's", 'taco bell', 'wendy', 'five guys', 'chipotle',
    'popeyes', 'arbys', "arby's", 'sonic', 'dairy queen', 'white castle',
    'jack in the box', 'carl', 'hardee', 'whataburger', 'in-n-out',
    'shake shack', 'chick-fil-a', 'panda express', 'el pollo loco'
  ];
  
  for (const chain of fastFoodChains) {
    if (name.includes(chain)) {
      return 'fast_food';
    }
  }
  
  // Priority 1: Check cuisine tag - restaurants have this
  if (tags.cuisine) {
    return 'restaurant';
  }
  
  // Priority 2: Check specific amenity tags
  if (tags.amenity) {
    const amenityMap = {
      'restaurant': 'restaurant',
      'fast_food': 'fast_food',
      'cafe': 'cafe',
      'bar': 'bar',
      'pub': 'pub',
      'food_court': 'food_court',
      'ice_cream': 'ice_cream',
      'pharmacy': 'pharmacy',
      'hospital': 'hospital',
      'bank': 'bank',
      'atm': 'atm',
      'fuel': 'gas_station',
      'parking': 'parking',
      'place_of_worship': 'place_of_worship',
      'theatre': 'theatre',
      'cinema': 'cinema',
      'nightclub': 'nightclub',
      'library': 'library',
    };
    
    if (amenityMap[tags.amenity]) {
      return amenityMap[tags.amenity];
    }
  }
  
  // Priority 3: Check shop tags
  if (tags.shop) {
    const shopMap = {
      'supermarket': 'supermarket',
      'convenience': 'convenience',
      'mall': 'mall',
      'bakery': 'bakery',
      'clothes': 'clothes',
      'books': 'shop',
      'electronics': 'shop',
      'furniture': 'shop',
      'jewelry': 'shop',
    };
    
    if (shopMap[tags.shop]) {
      return shopMap[tags.shop];
    }
    return 'shop';
  }
  
  // Priority 4: Check tourism tags
  if (tags.tourism) {
    const tourismMap = {
      'hotel': 'hotel',
      'hostel': 'hotel',
      'guest_house': 'hotel',
      'museum': 'museum',
      'attraction': 'attraction',
      'viewpoint': 'viewpoint',
    };
    
    if (tourismMap[tags.tourism]) {
      return tourismMap[tags.tourism];
    }
  }
  
  // Priority 5: Check leisure tags
  if (tags.leisure) {
    const leisureMap = {
      'park': 'park',
      'garden': 'park',
      'playground': 'playground',
      'sports_centre': 'sports_centre',
      'fitness_centre': 'gym',
      'stadium': 'stadium',
      'pitch': 'sports_centre',
      'swimming_pool': 'swimming_pool',
    };
    
    if (leisureMap[tags.leisure]) {
      return leisureMap[tags.leisure];
    }
  }
  
  // Priority 6: Check natural tags
  if (tags.natural) {
    const naturalMap = {
      'beach': 'beach',
      'sand': 'beach',
      'water': 'beach',
      'coastline': 'beach',
      'peak': 'peak',
      'waterfall': 'waterfall',
      'wood': 'park',
      'tree': 'park',
    };
    
    if (naturalMap[tags.natural]) {
      return naturalMap[tags.natural];
    }
  }
  
  // Priority 7: Analyze name for keywords
  const categoryKeywords = {
    // Fast Food - EXPANDED
    fast_food: ['burger', 'pizza', 'kebab', 'fries', 'wings', 'hot dog', 'sandwich', 'sub'],
    
    // Restaurants
    restaurant: ['restaurant', 'restauracja', 'bistro', 'grill', 'tavern', 'trattoria', 'eatery', 'diner', 'steakhouse'],
    
    // Cafes
    cafe: ['cafe', 'coffee', 'kawa', 'kawiarnia', 'cafeteria', 'espresso', 'starbucks', 'costa'],
    
    // Bars
    bar: ['bar', 'pub', 'drink', 'cocktail', 'lounge', 'piwiarnia', 'browar'],
    
    // Shopping
    supermarket: ['biedronka', 'lidl', 'carrefour', 'auchan', 'tesco', 'kaufland', 'żabka', 'zabka'],
    mall: ['mall', 'centrum handlowe', 'galeria'],
    
    // Other
    pharmacy: ['pharmacy', 'apteka'],
    bank: ['bank'],
    gas_station: ['shell', 'orlen', 'lotos', 'bp', 'station'],
  };
  
  for (const [category, keywords] of Object.entries(categoryKeywords)) {
    for (const keyword of keywords) {
      if (name.includes(keyword)) {
        return category;
      }
    }
  }
  
  // Priority 8: Check building type
  if (tags.building) {
    const buildingMap = {
      'hotel': 'hotel',
      'restaurant': 'restaurant',
      'retail': 'shop',
      'commercial': 'shop',
    };
    
    if (buildingMap[tags.building]) {
      return buildingMap[tags.building];
    }
  }

  return 'place';
};

// Modern SVG icon generator - EXPANDED with more categories
export const getModernPlaceIcon = (type) => {
  // Map all place types to the 6 app categories
  const categoryMap = {
    // Food & Drink
    restaurant: 'food_drink', cafe: 'food_drink', bar: 'food_drink',
    pub: 'food_drink', fast_food: 'food_drink', food_court: 'food_drink',
    ice_cream: 'food_drink', bakery: 'food_drink',
    // Nightlife
    nightclub: 'nightlife', club: 'nightlife',
    // Outdoors
    park: 'outdoors', garden: 'outdoors', playground: 'outdoors',
    beach: 'outdoors', peak: 'outdoors', waterfall: 'outdoors', nature: 'outdoors',
    // Active
    sports_centre: 'active', fitness_centre: 'active', gym: 'active',
    stadium: 'active', swimming_pool: 'active', pitch: 'active', sports: 'active',
    // Shopping
    supermarket: 'shopping', convenience: 'shopping', mall: 'shopping',
    marketplace: 'shopping', shop: 'shopping', clothes: 'shopping',
    // Entertainment
    museum: 'entertainment', gallery: 'entertainment', theatre: 'entertainment',
    cinema: 'entertainment', library: 'entertainment', attraction: 'entertainment',
    hotel: 'entertainment', monument: 'entertainment', castle: 'entertainment',
    viewpoint: 'entertainment', bank: 'entertainment', pharmacy: 'entertainment',
    hospital: 'entertainment', police: 'entertainment', post_office: 'entertainment',
    gas_station: 'entertainment', fuel: 'entertainment', parking: 'entertainment',
  };

  const categoryIcons = {
    food_drink:    { icon: '🍽️', bg: '#EA4335' },
    nightlife:     { icon: '🌙', bg: '#6A1B9A' },
    outdoors:      { icon: '🌿', bg: '#34A853' },
    active:        { icon: '⚡', bg: '#1E88E5' },
    shopping:      { icon: '🛍️', bg: '#FF9800' },
    entertainment: { icon: '🎭', bg: '#E91E63' },
    pin:           { icon: '📍', bg: '#757575' },
  };

  const category = categoryMap[type] || (type && type !== 'place' && type !== 'default' ? 'entertainment' : null);
  const config = category ? categoryIcons[category] : categoryIcons.pin;

  return `
    <svg width="48" height="48" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <filter id="shadow_${type}" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur in="SourceAlpha" stdDeviation="2"/>
          <feOffset dx="0" dy="2" result="offsetblur"/>
          <feComponentTransfer>
            <feFuncA type="linear" slope="0.3"/>
          </feComponentTransfer>
          <feMerge>
            <feMergeNode/>
            <feMergeNode in="SourceGraphic"/>
          </feMerge>
        </filter>
        <linearGradient id="glossy_${type}" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" style="stop-color:white;stop-opacity:0.6" />
          <stop offset="50%" style="stop-color:white;stop-opacity:0.1" />
          <stop offset="100%" style="stop-color:black;stop-opacity:0.1" />
        </linearGradient>
      </defs>
      <circle cx="24" cy="24" r="22" fill="white" filter="url(#shadow_${type})"/>
      <circle cx="24" cy="24" r="18" fill="${config.bg}"/>
      <text x="24" y="24" font-size="20" text-anchor="middle" dominant-baseline="central">${config.icon}</text>
      <circle cx="24" cy="24" r="18" fill="url(#glossy_${type})" opacity="0.3"/>
    </svg>
  `;
};